import React, { useState } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { useLocation as useAppLocation } from '../../contexts/LocationContext';
import { AgricultureChart } from '../charts/AgricultureChart';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import WasteTonnageChart from '../charts/WasteTonnageChart';
import WasteGHGChart from '../charts/WasteGHGChart';
import LandfillEmissionsStackedChart from '../charts/LandfillEmissionsStackedChart';
import OffroadEmissionsChart from '../charts/OffroadEmissionsChart';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { PageHeader } from '../layout/PageHeader';

export const Other: React.FC = () => {
  const routerLocation = useRouterLocation();
  const searchParams = new URLSearchParams(routerLocation.search);
  const { selectedLocation } = useAppLocation();
  const currentLocation = searchParams.get('location') || selectedLocation || 'Selected Location';

  // State for toggling different views
  const [activeWasteView, setActiveWasteView] = useState<string>('tonnage');
  const [activeWasteContentView, setActiveWasteContentView] = useState<string>('overview');
  const [activeAgricultureView, setActiveAgricultureView] = useState<string>('overview');
  const [activeOffroadView, setActiveOffroadView] = useState<string>('overview');
  const [wasteMetricTons, setWasteMetricTons] = useState<string>('—');
  const [wasteMetricPerService, setWasteMetricPerService] = useState<string>('—');
  const [wasteMetricGHG, setWasteMetricGHG] = useState<string>('—');
  const [wasteMetricTonsYear, setWasteMetricTonsYear] = useState<number | null>(null);
  const [wasteMetricGHGYear, setWasteMetricGHGYear] = useState<number | null>(null);
  const [wasteNarrative, setWasteNarrative] = useState<string>('Loading waste data...');
  const [wasteNarrativeGHG, setWasteNarrativeGHG] = useState<string>('Loading GHG data...');
  const [agMetricTotal, setAgMetricTotal] = useState<string>('—');
  const [agMetricLargest, setAgMetricLargest] = useState<string>('—');
  const [agMetricTrend, setAgMetricTrend] = useState<string>('—');
  const [agMetricTrendColor, setAgMetricTrendColor] = useState<string>('#10b981');
  const [agMetricTrendDescription, setAgMetricTrendDescription] = useState<string>('Agricultural emissions by source are shown for the county of the selected location.');
  const [countyName, setCountyName] = useState<string>('');

  const toggleWasteView = (view: string) => {
    setActiveWasteView(view);
  };

  const toggleAgricultureView = (view: string) => {
    setActiveAgricultureView(view);
  };


  // Fetch latest waste metrics for KeyMetrics
  React.useEffect(() => {
    const loadWasteMetrics = async () => {
      try {
        await supabaseLocationService.initialize();
        const geofips = await supabaseLocationService.getGeofips(currentLocation);
        if (!geofips) {
          setWasteNarrative('No location selected.');
          return;
        }
        // Fetch all waste tonnage data for trend calculation
        const { data: wasteRows } = await supabaseClient
          .from('total_waste')
          .select('year, tons_waste, waste_per_service_population')
          .eq('geofips', geofips)
          .order('year', { ascending: true });

        // Fetch California average waste per service population (geofips = 6) for comparison
        const { data: californiaWasteData } = await supabaseClient
          .from('total_waste')
          .select('year, waste_per_service_population')
          .eq('geofips', 6)
          .order('year', { ascending: true });
        
        let trendTons: number | null = null;
        let trendTonsYear: number | null = null;

        if (wasteRows && wasteRows.length > 0) {
          const latest = wasteRows[wasteRows.length - 1];
          setWasteMetricTons((Number(latest.tons_waste || 0)).toLocaleString());
          setWasteMetricPerService((Number(latest.waste_per_service_population || 0)).toLocaleString());
          setWasteMetricTonsYear(latest.year);
          
          // Calculate trend for tons
          if (wasteRows.length > 1) {
            const firstYear = wasteRows[0].year;
            const firstTons = Number(wasteRows[0].tons_waste || 0);
            const latestTons = Number(latest.tons_waste || 0);
            if (firstTons > 0) {
              const changePercent = ((latestTons - firstTons) / firstTons) * 100;
              trendTons = changePercent;
              trendTonsYear = firstYear;
            }
          }
        } else {
          setWasteMetricTons('—');
          setWasteMetricPerService('—');
        }
        
        // Fetch all landfill emissions data for trend calculation
        const { data: ghgRows } = await supabaseClient
          .from('landfill_emissions_filtered')
          .select('year, emissions')
          .eq('geofips', geofips)
          .order('year', { ascending: true });
        
        // Sum emissions by year
        const emissionsByYear: Record<number, number> = {};
        (ghgRows || []).forEach((r: any) => {
          const y = r.year;
          emissionsByYear[y] = (emissionsByYear[y] || 0) + Number(r.emissions || 0);
        });
        const emissionsYears = Object.keys(emissionsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
        
        if (emissionsYears.length > 0) {
          const latestYear = emissionsYears[emissionsYears.length - 1];
          const latestEmissions = emissionsByYear[latestYear];
          setWasteMetricGHG((latestEmissions).toLocaleString(undefined, { maximumFractionDigits: 0 }));
          setWasteMetricGHGYear(latestYear);
          
        } else {
          setWasteMetricGHG('—');
        }
        
        // Generate smart narrative for Tonnage chart (no GHG emissions info)
        const latestWasteYear = wasteRows && wasteRows.length > 0 ? wasteRows[wasteRows.length - 1].year : null;
        const latestTons = wasteRows && wasteRows.length > 0 ? Number(wasteRows[wasteRows.length - 1].tons_waste || 0) : 0;
        const latestPerService = wasteRows && wasteRows.length > 0 ? Number(wasteRows[wasteRows.length - 1].waste_per_service_population || 0) : 0;
        
        // Get California average for the same year
        const californiaLatest = californiaWasteData && californiaWasteData.length > 0 && latestWasteYear
          ? californiaWasteData.find((r: any) => r.year === latestWasteYear) || californiaWasteData[californiaWasteData.length - 1]
          : null;
        const californiaPerService = californiaLatest ? Number(californiaLatest.waste_per_service_population || 0) : 0;
        
        let tonnageNarrative = '';
        if (latestTons > 0 && latestWasteYear) {
          tonnageNarrative = `${currentLocation} generated ${latestTons.toLocaleString()} tons of waste in ${latestWasteYear}`;
          if (latestPerService > 0) {
            tonnageNarrative += `, equivalent to ${latestPerService.toFixed(2)} tons per service population (residents plus employees)`;
            
            // Compare to California average
            if (californiaPerService > 0) {
              const diffPercent = ((latestPerService - californiaPerService) / californiaPerService) * 100;
              if (diffPercent > 0) {
                tonnageNarrative += `, which is ${Math.abs(diffPercent).toFixed(1)}% above the statewide average of ${californiaPerService.toFixed(2)} tons per service population`;
              } else if (diffPercent < 0) {
                tonnageNarrative += `, which is ${Math.abs(diffPercent).toFixed(1)}% below the statewide average of ${californiaPerService.toFixed(2)} tons per service population`;
              } else {
                tonnageNarrative += `, matching the statewide average of ${californiaPerService.toFixed(2)} tons per service population`;
              }
            }
            tonnageNarrative += '.';
          } else {
            tonnageNarrative += '.';
          }
          
          if (trendTons !== null && trendTonsYear) {
            tonnageNarrative += ` Total waste tonnage has ${trendTons > 0 ? 'increased' : 'decreased'} by ${Math.abs(trendTons).toFixed(1)}% since ${trendTonsYear}`;
            if (trendTons < 0) {
              tonnageNarrative += ', indicating progress in waste reduction.';
            } else if (trendTons > 5) {
              tonnageNarrative += ', which may require additional waste reduction strategies.';
            } else {
              tonnageNarrative += '.';
            }
          }
        } else {
          tonnageNarrative = `No waste data available for ${currentLocation}.`;
        }
        
        setWasteNarrative(tonnageNarrative || 'Loading waste data...');

        // Generate smart narrative for GHG chart (emissions and intensity focus)
        // Calculate GHG emissions using same method as WasteGHGChart (waste_tons × emission_factor)
        let ghgNarrative = '';
        try {
          // Get location info to determine county geofips for landfill data
          const { data: locationData } = await supabaseClient
            .from('population')
            .select('geography_type, county')
            .eq('geofips', geofips)
            .limit(1);

          let resolvedGeofips = geofips;
          let countyGeofips = geofips;

          if (locationData && locationData.length > 0 && locationData[0].geography_type === 'City') {
            const countyName = locationData[0].county;
            if (countyName) {
              const { data: countyData } = await supabaseClient
                .from('population')
                .select('geofips')
                .eq('location_name', countyName)
                .eq('geography_type', 'County')
                .limit(1);

              if (countyData && countyData.length > 0) {
                countyGeofips = countyData[0].geofips;
              }
            }
          }

          // Fetch landfill emissions data at county level to calculate emission factors
          const { data: landfillData } = await supabaseClient
            .from('landfill_emissions_full')
            .select('year, waste_tons, ghg_emissions')
            .eq('geofips', countyGeofips)
            .order('year', { ascending: true });

          // Calculate county-specific emission factors by year
          const countyTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
          (landfillData || []).forEach((r: any) => {
            const year = r.year;
            if (!countyTotalsByYear[year]) {
              countyTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
            }
            countyTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
            countyTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
          });

          const emissionFactorsByYear: Record<number, number> = {};
          Object.keys(countyTotalsByYear).forEach(yearStr => {
            const year = parseInt(yearStr);
            const totals = countyTotalsByYear[year];
            if (totals.totalWaste > 0) {
              emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
            }
          });

          // Check if we have valid county emission factors, if not fetch statewide average by aggregating all California counties
          const hasValidCountyFactors = Object.keys(emissionFactorsByYear).length > 0 && 
            Object.values(emissionFactorsByYear).some(factor => factor > 0);
          
          if (!hasValidCountyFactors) {
            console.log('Other page: No valid county emission factors, calculating statewide average from all California counties');
            
            // Fetch all California county data (geofips starting with 60, typically 4-digit county codes)
            // This aggregates all counties to get California-wide averages
            const { data: californiaLandfillData } = await supabaseClient
              .from('landfill_emissions_full')
              .select('year, waste_tons, ghg_emissions, geofips')
              .gte('geofips', 6000)
              .lt('geofips', 7000) // California counties are typically 6000-6999
              .order('year', { ascending: true });

            if (californiaLandfillData && californiaLandfillData.length > 0) {
              // Calculate statewide emission factors
              const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
              californiaLandfillData.forEach((r: any) => {
                const year = r.year;
                if (!stateTotalsByYear[year]) {
                  stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
                }
                stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
                stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
              });

              // Calculate statewide emission factors
              Object.keys(stateTotalsByYear).forEach(yearStr => {
                const year = parseInt(yearStr);
                const totals = stateTotalsByYear[year];
                if (totals.totalWaste > 0) {
                  emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
                }
              });
              
              console.log('Other page: Using statewide average emission factors');
            }
          } else {
            // If we have some county factors but not all years, fill missing years with statewide average
            const allRequiredYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
            const missingYears = allRequiredYears.filter(year => !emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0);
            
            if (missingYears.length > 0) {
              console.log(`Other page: Filling ${missingYears.length} missing years with statewide average`);
              
              // Fetch all California county data to calculate statewide averages
              const { data: californiaLandfillData } = await supabaseClient
                .from('landfill_emissions_full')
                .select('year, waste_tons, ghg_emissions, geofips')
                .gte('geofips', 6000)
                .lt('geofips', 7000) // California counties are typically 6000-6999
                .order('year', { ascending: true });

              if (californiaLandfillData && californiaLandfillData.length > 0) {
                // Calculate statewide emission factors for missing years
                const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
                californiaLandfillData.forEach((r: any) => {
                  const year = r.year;
                  if (missingYears.includes(year)) {
                    if (!stateTotalsByYear[year]) {
                      stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
                    }
                    stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
                    stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
                  }
                });

                // Fill in missing years with statewide factors
                Object.keys(stateTotalsByYear).forEach(yearStr => {
                  const year = parseInt(yearStr);
                  const totals = stateTotalsByYear[year];
                  if (totals.totalWaste > 0 && (!emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0)) {
                    emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
                  }
                });
              }
            }
          }

          // Project carbon intensity linearly for years 2020-2023 if data not available
          const factorYearsForProjection = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
          if (factorYearsForProjection.length > 0) {
            const latestFactorYear = Math.max(...factorYearsForProjection);
            const lastFactor = emissionFactorsByYear[latestFactorYear];
            const secondLastFactor = factorYearsForProjection.length > 1 ? emissionFactorsByYear[factorYearsForProjection[factorYearsForProjection.length - 2]] : null;

            if (latestFactorYear < 2023) {
              if (secondLastFactor !== null) {
                const yearsDiff = latestFactorYear - factorYearsForProjection[factorYearsForProjection.length - 2];
                const factorDiff = lastFactor - secondLastFactor;
                const trendPerYear = factorDiff / yearsDiff;

                for (let year = 2020; year <= 2023; year++) {
                  if (!emissionFactorsByYear[year] || year > latestFactorYear) {
                    const yearsToProject = year - latestFactorYear;
                    emissionFactorsByYear[year] = lastFactor + (trendPerYear * yearsToProject);
                  }
                }
              } else {
                for (let year = 2020; year <= 2023; year++) {
                  if (!emissionFactorsByYear[year] || year > latestFactorYear) {
                    emissionFactorsByYear[year] = lastFactor;
                  }
                }
              }
            }
          }

          // Fetch population data for backcasting waste tons
          const { data: populationData } = await supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', resolvedGeofips)
            .order('year', { ascending: true });

          // Build population by year
          const populationByYear: Record<number, number> = {};
          (populationData || []).forEach((r: any) => {
            populationByYear[r.year] = Number(r.population || 0);
          });

          // Aggregate waste tons by year
          const wasteTonsByYear: Record<number, number> = {};
          (wasteRows || []).forEach((r: any) => {
            const year = r.year;
            wasteTonsByYear[year] = (wasteTonsByYear[year] || 0) + Number(r.tons_waste || 0);
          });

          // Backcast waste tons to 2005 using population ratios
          const wasteYears = Object.keys(wasteTonsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
          const earliestWasteYear = wasteYears.length > 0 ? Math.min(...wasteYears) : null;

          if (earliestWasteYear && earliestWasteYear > 2005 && populationByYear[earliestWasteYear] && populationByYear[earliestWasteYear] > 0) {
            const baseTons = wasteTonsByYear[earliestWasteYear];
            const basePopulation = populationByYear[earliestWasteYear];

            // Backcast years before earliest waste data year
            for (let year = 2005; year < earliestWasteYear; year++) {
              const yearPopulation = populationByYear[year] || basePopulation;
              const populationRatio = yearPopulation / basePopulation;
              
              // Backcast tons using population ratio
              wasteTonsByYear[year] = baseTons * populationRatio;
            }
          }

          // Backcast emission factors to 2005 if needed
          const factorYears = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
          const earliestFactorYear = factorYears.length > 0 ? Math.min(...factorYears) : null;

          if (earliestFactorYear && earliestFactorYear > 2005) {
            const baseFactor = emissionFactorsByYear[earliestFactorYear];
            
            // Use earliest factor for all years before earliest data year
            for (let year = 2005; year < earliestFactorYear; year++) {
              emissionFactorsByYear[year] = baseFactor;
            }
          }

          // Calculate GHG emissions = waste_tons × emission_factor for each year (2005-2023)
          const calculatedEmissionsByYear: Record<number, number> = {};
          const allYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
          
          allYears.forEach(year => {
            const wasteTons = wasteTonsByYear[year] || 0;
            const emissionFactor = emissionFactorsByYear[year] || 0;
            
            if (wasteTons > 0 && emissionFactor > 0) {
              calculatedEmissionsByYear[year] = wasteTons * emissionFactor;
            } else {
              calculatedEmissionsByYear[year] = 0;
            }
          });

          // Find latest emissions year and calculate trends
          const emissionsYears = Object.keys(calculatedEmissionsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
          if (emissionsYears.length > 0) {
            const latestEmissionsYear = Math.max(...emissionsYears);
            const latestEmissions = calculatedEmissionsByYear[latestEmissionsYear];
            const latestIntensity = emissionFactorsByYear[latestEmissionsYear] || 0;

            ghgNarrative = `Landfill greenhouse gas emissions in ${currentLocation} totaled ${latestEmissions.toLocaleString(undefined, { maximumFractionDigits: 0 })} metric tons CO₂e in ${latestEmissionsYear}`;
            ghgNarrative += `, with a carbon intensity of ${latestIntensity.toFixed(3)} tons CO₂e per ton of waste.`;

            // Calculate emissions trend
            if (emissionsYears.length > 1) {
              const firstEmissionsYear = Math.min(...emissionsYears);
              const firstEmissions = calculatedEmissionsByYear[firstEmissionsYear];
              if (firstEmissions > 0) {
                const emissionsChange = ((latestEmissions - firstEmissions) / firstEmissions) * 100;
                ghgNarrative += ` Emissions have ${emissionsChange > 0 ? 'increased' : 'decreased'} by ${Math.abs(emissionsChange).toFixed(1)}% since ${firstEmissionsYear}`;
                if (emissionsChange < 0) {
                  ghgNarrative += ', indicating progress in reducing waste-related GHG emissions.';
                } else {
                  ghgNarrative += '.';
                }
              }
            }

            // Calculate intensity trend
            const intensityYears = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
            if (intensityYears.length > 1) {
              const firstIntensityYear = Math.min(...intensityYears);
              const firstIntensity = emissionFactorsByYear[firstIntensityYear];
              if (firstIntensity > 0) {
                const intensityChange = ((latestIntensity - firstIntensity) / firstIntensity) * 100;
                ghgNarrative += ` Carbon intensity has ${intensityChange > 0 ? 'increased' : 'decreased'} by ${Math.abs(intensityChange).toFixed(1)}% since ${firstIntensityYear}`;
                if (intensityChange < 0) {
                  ghgNarrative += ', reflecting improved landfill management and methane capture practices.';
                } else {
                  ghgNarrative += '.';
                }
              }
            }
          } else {
            ghgNarrative = `No GHG emissions data available for ${currentLocation}.`;
          }
        } catch (e) {
          console.warn('Error generating GHG narrative:', e);
          ghgNarrative = 'Error loading GHG emissions data.';
        }

        setWasteNarrativeGHG(ghgNarrative || 'Loading GHG data...');
      } catch (e) {
        console.warn('Error loading waste metrics:', e);
        setWasteNarrative('Error loading waste data.');
      }
    };
    loadWasteMetrics();
  }, [currentLocation]);

  // Fetch agricultural emissions metrics
  React.useEffect(() => {
    const loadAgMetrics = async () => {
      try {
        await supabaseLocationService.initialize();
        
        // Get county name - handle both city and county selections
        const geofips = await supabaseLocationService.getGeofips(currentLocation);
        let resolvedCountyName = currentLocation;
        
        if (geofips) {
          // Check if location is a city or county
          const { data: locationData } = await supabaseClient
            .from('population')
            .select('geography_type, county, location_name')
            .eq('geofips', geofips)
            .limit(1);
          
          if (locationData && locationData.length > 0) {
            if (locationData[0].geography_type === 'City') {
              // If it's a city, get the county name
              const countyName = locationData[0].county;
              resolvedCountyName = countyName || supabaseLocationService.getCountyForCity(currentLocation) || currentLocation;
            } else if (locationData[0].geography_type === 'County') {
              // If it's a county, use the location name
              resolvedCountyName = locationData[0].location_name || currentLocation;
            }
          } else {
            // Fallback to getCountyForCity
            resolvedCountyName = supabaseLocationService.getCountyForCity(currentLocation) || currentLocation;
          }
        } else {
          // Fallback to getCountyForCity
          resolvedCountyName = supabaseLocationService.getCountyForCity(currentLocation) || currentLocation;
        }
        
        setCountyName(resolvedCountyName);
        
        const countyName = resolvedCountyName;
        const baseCounty = (countyName || '').replace(' County', '').trim();
        
        // Find county geofips
        let rows: any[] | null = null;
        const q1 = await supabaseClient
          .from('ag_emissions')
          .select('*')
          .or(`county.ilike.%${baseCounty}%,geo_name.ilike.%${baseCounty}%,geo_name.ilike.%${baseCounty} County%`);
        if (q1.data && q1.data.length > 0) rows = q1.data;
        
        if (!rows || rows.length === 0) {
          const q2 = await supabaseClient
            .from('ag_emissions')
            .select('*')
            .eq('state', 'California')
            .ilike('county', `%${baseCounty}%`);
          if (q2.data && q2.data.length > 0) rows = q2.data;
        }
        
        let countyGeofips: string | null = null;
        if (rows && rows.length > 0) {
          const first = rows.find(r => r.geofips && String(r.geofips).length <= 5) || rows[0];
          countyGeofips = String(first.geofips || '').trim();
          if (countyGeofips) {
            const { data: rowsByFips } = await supabaseClient
              .from('ag_emissions')
              .select('*')
              .eq('geofips', countyGeofips);
            if (rowsByFips && rowsByFips.length > 0) rows = rowsByFips;
          }
        }
        
        if (!rows || rows.length === 0) {
          setAgMetricTotal('—');
          setAgMetricLargest('—');
          setAgMetricTrend('—');
          setAgMetricTrendDescription('No agricultural emissions data available for this location.');
          return;
        }
        
        // Calculate metrics
        const years = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
        const categoriesMap: Record<string, number[]> = {};
        (rows || []).forEach((r: any) => {
          const cat = r.var_name2 || 'Other';
          if (!categoriesMap[cat]) categoriesMap[cat] = years.map(() => 0);
          years.forEach((y, idx) => {
            const val = Number(r[String(y)]) || 0;
            categoriesMap[cat][idx] += val * 1_000_000;
          });
        });
        
        // Combine livestock
        const livestockKeys = ['head_cattle','head_poultry','head_swine','head_other_livestock'];
        const hasLivestock = livestockKeys.some(k => categoriesMap[k]);
        if (hasLivestock) {
          const livestockTotals = years.map((_, idx) => 0);
          livestockKeys.forEach(key => {
            const arr = categoriesMap[key];
            if (arr) {
              arr.forEach((v, i) => livestockTotals[i] += v);
              delete categoriesMap[key];
            }
          });
          categoriesMap['Livestock'] = livestockTotals;
        }
        
        // Total for 2023 (last year index)
        const lastIdx = years.length - 1;
        const total2023 = Object.values(categoriesMap).reduce((sum, arr) => sum + (arr[lastIdx] || 0), 0);
        setAgMetricTotal(total2023.toLocaleString(undefined, { maximumFractionDigits: 0 }));
        
        // Largest source in 2023
        let largestSource = '—';
        let largestValue = 0;
        Object.entries(categoriesMap).forEach(([cat, arr]) => {
          if (arr[lastIdx] > largestValue) {
            largestValue = arr[lastIdx];
            largestSource = cat;
          }
        });
        setAgMetricLargest(largestSource !== '—' ? largestSource : '—');
        
        // Trend: total 2023 vs 2005
        const total2005 = Object.values(categoriesMap).reduce((sum, arr) => sum + (arr[0] || 0), 0);
        const change = total2023 - total2005;
        const changePercent = total2005 > 0 ? ((change / total2005) * 100) : 0;
        const trendText = changePercent >= 0 ? `+${changePercent.toFixed(1)}%` : `${changePercent.toFixed(1)}%`;
        setAgMetricTrend(trendText);
        setAgMetricTrendColor(changePercent < 0 ? '#10b981' : '#ef4444');
        
        const trendDesc = changePercent < 0 
          ? `Agricultural emissions decreased by ${Math.abs(changePercent).toFixed(1)}% from 2005 to 2023, indicating progress in reducing agricultural GHG emissions.`
          : changePercent > 0
          ? `Agricultural emissions increased by ${changePercent.toFixed(1)}% from 2005 to 2023.`
          : 'Agricultural emissions have remained relatively stable from 2005 to 2023.';
        setAgMetricTrendDescription(trendDesc);
      } catch (e) {
        setAgMetricTotal('—');
        setAgMetricLargest('—');
        setAgMetricTrend('—');
        setAgMetricTrendDescription('Error loading agricultural emissions data.');
      }
    };
    loadAgMetrics();
  }, [currentLocation]);

  return (
    <div>
      <PageHeader 
        title="Other"
        subtitle="Waste management, agriculture, and land use data for the selected location."
        variant="other"
      />

      {/* Waste Section - standard layout with chart selector */}
      <ChartLayout
        title="Waste Management"
        description="Waste generation and landfill GHG emissions for the selected location."
        chartType="line"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value={wasteMetricTons} 
              label={wasteMetricTonsYear ? `Tons of Waste (${wasteMetricTonsYear})` : "Latest Tons of Waste"} 
              color="#2563eb" 
            />
            <KeyMetric 
              value={wasteMetricPerService} 
              label="Waste per Service Pop" 
              color="#10b981"
            />
            <KeyMetric 
              value={wasteMetricGHG} 
              label={wasteMetricGHGYear ? `Landfill GHG (${wasteMetricGHGYear}, tCO₂e)` : "Latest Landfill GHG (tCO₂e)"} 
              color="#ef4444" 
            />
          </KeyMetrics>
        }
        chartContent={
          activeWasteView === 'ghg' ? (
            <WasteGHGChart location={currentLocation} />
          ) : activeWasteView === 'stacked' ? (
            <LandfillEmissionsStackedChart location={currentLocation} />
          ) : (
            <WasteTonnageChart location={currentLocation} />
          )
        }
        chartSelector={{
          options: [
            { value: 'tonnage', label: 'Tonnage' },
            { value: 'ghg', label: 'GHGs' },
            { value: 'stacked', label: 'Emissions by Facility' }
          ],
          value: activeWasteView,
          onChange: toggleWasteView
        }}
        overviewContent={
          <OverviewContent
            title="Waste Overview"
            description={activeWasteView === 'ghg' 
              ? "This chart displays calculated landfill greenhouse gas emissions over time. GHG emissions are calculated by multiplying waste tons (from total_waste) by a county-specific emission factor. The emission factor is calculated as SUM(GHG Emissions) ÷ SUM(waste_tons) by county and year from landfill_emissions_full. Carbon intensity (the emission factor in tons CO₂e per ton waste) is shown on the secondary y-axis, typically ranging around 0.2 but varying by year."
              : activeWasteView === 'stacked'
              ? "This stacked area chart shows GHG emissions broken down by individual landfill facility receiving waste from the municipality over time. Note: This chart is NOT used in the sector-based GHG inventory. The data are used to calculate GHG emissions intensities for each municipality based on county-wide averages. Using this approach city-specific GHG intensities may be calculated, but are often subject to wide variability. Landfills are regulated by the California Air Resources Board and landfill management practices are therefore not typically within the control of local governments. Emissions data comes from US EPA, combined with waste tonnage by jurisdiction from CalRecycle Jurisdiction of Origin Waste Disposal database."
              : "This section shows total waste tonnage and waste per service population over time. Per-service population equals residents plus employees, providing a normalized metric for waste generation."
            }
          >
            <p>{activeWasteView === 'ghg' ? wasteNarrativeGHG : wasteNarrative}</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Waste contributes to GHGs primarily through landfill methane; tracking tonnage and emissions supports reduction strategies like organics diversion."
          />
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Waste burdens and facility siting can disproportionately affect some communities; equitable policies focus on access to reduction programs and healthy environments."
          />
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description={activeWasteView === 'stacked'
              ? "The landfill emissions calculation combines data on waste sent to landfills by jurisdiction from CalRecycle Jurisdiction of Origin Waste Disposal database and GHG emissions by landfill from US EPA. Location resolved by geofips."
              : activeWasteView === 'ghg'
              ? "Waste tonnage data comes from CalRecycle. Landfill facility emissions data comes from the US EPA Greenhouse Gas Reporting Program (GHGRP). Location resolved by geofips."
              : "Waste tonnage data comes from CalRecycle. Landfill facility emissions data comes from the US EPA Greenhouse Gas Reporting Program (GHGRP). Location resolved by geofips. Per-service population equals residents plus employees."
            }
            dataSources={activeWasteView === 'stacked'
              ? [
                  'CalRecycle Jurisdiction of Origin Waste Disposal database - Waste tonnage by jurisdiction',
                  'US EPA Greenhouse Gas Reporting Program (GHGRP) - Landfill facility GHG emissions'
                ]
              : activeWasteView === 'ghg'
              ? [
                  'CalRecycle - Waste tonnage data from total_waste table',
                  'US EPA Greenhouse Gas Reporting Program (GHGRP) - Landfill facility emissions from landfill_emissions_full table'
                ]
              : [
                  'CalRecycle - Waste tonnage data',
                  'US EPA Greenhouse Gas Reporting Program (GHGRP) - Landfill facility emissions'
                ]
            }
            methodology={activeWasteView === 'stacked'
              ? [
                  'Resolve geofips for selected location (use county geofips for cities)',
                  'Query waste tonnage by jurisdiction from CalRecycle Jurisdiction of Origin Waste Disposal database',
                  'Query GHG emissions by landfill facility from US EPA GHGRP data',
                  'Filter to show only facilities with data for the selected location',
                  'Aggregate and render by landfill facility as stacked area chart',
                  'Display emissions in metric tons CO₂e',
                  '',
                  'Note: This chart is for informational purposes only and is NOT used in the sector-based GHG inventory calculation. Landfill emissions are regulated by the California Air Resources Board and landfill management practices are not typically within local government control.'
                ]
              : activeWasteView === 'ghg'
                ? [
                    'Resolve geofips for selected location (use county geofips for landfill emissions data)',
                    'Query waste tons by year from total_waste table for selected location',
                    'Query waste_tons and ghg_emissions from landfill_emissions_full at county level',
                    'Calculate county-specific emission factor per year: SUM(ghg_emissions) ÷ SUM(waste_tons) by county and year',
                    'Project carbon intensity linearly for years 2020-2023 if data not available',
                    'Calculate GHG emissions: waste_tons × emission_factor for each year',
                    'Display calculated emissions (left axis) and carbon intensity/emission factor (right axis)',
                    'Carbon intensity typically ranges around 0.2 tons CO₂e per ton waste but varies by year'
                  ]
                : [
                    'Resolve geofips for selected location',
                    'Query waste tonnage and per-service population from CalRecycle data',
                    'Query landfill facility emissions from EPA GHGRP data',
                    'Aggregate and render by year',
                    'Show tons and per-service population on separate y-axes',
                    'Display landfill emissions in metric tons CO₂e'
                  ]
            }
          />
        }
        activeView={activeWasteContentView}
        onViewChange={setActiveWasteContentView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
      />

      {/* Agriculture Section */}
      <ChartLayout
        title="Agriculture"
        description="Agricultural greenhouse gas emissions by source for the county of the selected location."
        chartType="bar"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric value={agMetricTotal} label="Total Ag Emissions (2023, tCO₂e)" color="#1e3a8a" />
            <KeyMetric value={agMetricLargest} label="Largest Source (2023)" color="#10b981" />
            <KeyMetric value={agMetricTrend} label="Change Since 2005" color={agMetricTrendColor} />
          </KeyMetrics>
        }
        chartContent={<AgricultureChart location={currentLocation} />}
        overviewContent={
          <OverviewContent
            title="Agriculture Overview"
            description="This chart displays agricultural greenhouse gas emissions by source category (e.g., livestock, fertilizer, rice cultivation, crop burning) for the county of the selected location from 2005 to 2023."
          >
            <p>
              Agricultural emissions in {countyName || 'the county'} totaled {agMetricTotal} metric tons CO₂e in 2023, with {agMetricLargest} as the largest
              source. {agMetricTrendDescription}
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Agricultural practices significantly impact greenhouse gas emissions, water use, and carbon sequestration potential."
          >
            <div className="methodology-details-simple">
              <h5>Emissions Reduction</h5>
              <p>Sustainable agriculture practices reduce synthetic fertilizer use, improve soil health, and decrease agricultural emissions.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Carbon Sequestration</h5>
              <p>Healthy soils and perennial crops can sequester carbon, contributing to net-negative emissions in the agricultural sector.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Sustainable agriculture practices should be accessible to farmers of all scales and backgrounds."
          >
            <div className="methodology-details-simple">
              <h5>Small Farm Access</h5>
              <p>Technical assistance and financial support for sustainable practices should prioritize small and beginning farmers.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Market Access</h5>
              <p>Local food systems and organic markets should be accessible to all communities, supporting both producers and consumers.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Agricultural data comes from USDA surveys, state agricultural statistics, and local farming program reports."
            dataSources={[
              'USDA Census of Agriculture and annual surveys',
              'California Department of Food and Agriculture data',
              'Local agricultural extension program reports',
              'Organic certification and sustainability program data'
            ]}
            methodology={[
              'Organic adoption calculated as certified organic acres / total agricultural acres',
              'Water efficiency measured as crop yield per unit of water applied',
              'Sustainable practices include organic, regenerative, and climate-smart agriculture',
              'Data aggregated from farm-level surveys and program participation records'
            ]}
          />
        }
        activeView={activeAgricultureView}
        onViewChange={toggleAgricultureView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
      />

      {/* Offroad Emissions Section */}
      <ChartLayout
        title="Offroad Emissions"
        description="Greenhouse gas emissions from offroad equipment and vehicles by category over time."
        chartType="line"
        chartContent={<OffroadEmissionsChart location={currentLocation} />}
        overviewContent={
          <OverviewContent
            title="Offroad Emissions Overview"
            description="This chart displays greenhouse gas emissions from offroad equipment and vehicles broken down by category (e.g., construction equipment, agricultural machinery, locomotives, marine vessels) from 2000 to 2050."
          >
            <p>
              Offroad emissions include a wide range of non-road mobile sources such as construction and mining equipment, 
              agricultural machinery, locomotives, marine vessels, and airport ground support equipment. These emissions 
              are significant contributors to local air quality and greenhouse gas inventories.
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Offroad equipment emissions represent a significant portion of transportation-related GHGs and can be reduced through electrification, efficiency improvements, and operational changes."
          >
            <div className="methodology-details-simple">
              <h5>Electrification</h5>
              <p>Many offroad equipment categories can be electrified, including construction equipment, airport ground support, and cargo handling equipment.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Efficiency Improvements</h5>
              <p>Modern equipment with improved fuel efficiency and emission controls can significantly reduce offroad emissions.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Operational Changes</h5>
              <p>Reducing equipment idle time, optimizing operations, and using alternative fuels can lower emissions from offroad sources.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Offroad emissions can disproportionately affect communities near ports, airports, construction sites, and industrial areas."
          >
            <div className="methodology-details-simple">
              <h5>Environmental Justice</h5>
              <p>Communities near ports, airports, and industrial facilities often bear a disproportionate burden of offroad emissions and should be prioritized for emission reduction efforts.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Workforce Transition</h5>
              <p>Transitioning to cleaner offroad equipment should include training and support for workers to ensure equitable access to new technologies and job opportunities.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Offroad emissions data comes from California Air Resources Board (CARB) EMFAC (EMission FACtors) model for offroad mobile sources."
            dataSources={[
              'CARB EMFAC Offroad v2021 - Raw exhaust emissions data',
              'Supabase offroad_emissions table - Processed and allocated emissions by location, year, and category'
            ]}
            methodology={[
              'Emissions calculated using EMFAC emission factors and activity data (vehicle miles traveled, hours of operation)',
              'Data covers years 2000-2050 with projections based on EMFAC forecasting ratios',
              'Emissions allocated to counties and cities based on population share for relevant categories',
              'Some categories (Agricultural, Airport Ground Support, Ocean Going Vessels) remain at county level only',
              'Data extrapolated and interpolated to ensure complete time series coverage'
            ]}
          />
        }
        activeView={activeOffroadView}
        onViewChange={setActiveOffroadView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
      />

    </div>
  );
};
